import { ReactNode, useMemo } from "react";
import FeedTable from "../components/FeedTable";
import NextUnlocks from "../components/NextUnlocks";
import PageLayout from "../components/PageLayout";
import PinIcon from "../components/PinIcon";
import { useLens } from "../lens/LensProvider";
import { ACHIEVEMENT_ICONS, DEFAULT_ACHIEVEMENT_ICON } from "../constants/achievementIcons";
import { AIRFRAME_PHOTOS } from "../constants/airframePhotos";
import { GAUGE_BY_ID } from "../constants/gauges";
import { PhotoKey } from "../constants/photos";
import usePilotStats, { PilotStatsData } from "../hooks/usePilotStats";
import { usePublicData } from "../hooks/usePublicData";
import { fetchAchievements } from "../services/achievementsService";
import { fetchFeed } from "../services/feedService";
import { fetchGaugeSummary } from "../services/gaugesService";
import { fetchPilot } from "../services/pilotsService";
import { formatDurationHoursRounded, formatRelativeTime } from "../utils/formatters";
import { ordinal } from "../utils/ordinal";

const SECTION_LIMIT = 5;
const ACTIVITY_LIMIT = 8;

const UNIT_FAMILY_TO_PHOTO_GROUP: Record<string, string> = {
  "f/a-18": "hornet", "f-16": "viper", "f-14": "tomcat", "ah-64": "apache", "oh-58": "kiowa_warrior",
  "f-15e": "strike_eagle",
  "a-10": "warthog", "av-8b": "harrier", "f-4": "phantom", "ka-50": "blackshark", "mi-24": "hind",
  "mi-8": "hip", "uh-1": "huey", "mig-21": "mig21", "mig-29": "fulcrum", "ajs37": "viggen",
  "c-130": "hercules", "mirage f1": "mirage_f1", "mirage 2000": "mirage_2000",
};

function primaryAirframeGroup(stats: PilotStatsData): string | null {
  const candidate = stats.durationByAirframe[0]?.tags.unit_family ?? stats.killsByAirframe[0]?.tags.killer_unit_family ?? null;
  return candidate ? UNIT_FAMILY_TO_PHOTO_GROUP[String(candidate).toLowerCase()] ?? null : null;
}

const encode = encodeURIComponent;

function Section({ title, href, wide = false, children }: { title: string; href: string; wide?: boolean; children: ReactNode }) {
  return (
    <section className={"panel profile-section" + (wide ? " is-wide" : "")}>
      <div className="dashboard-widget-header">
        <h2>{title}</h2>
        <a href={href}>View all →</a>
      </div>
      {children}
    </section>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="dashboard-empty-row">{children}</p>;
}

function Unavailable({ what, retry }: { what: string; retry: () => void }) {
  return <div role="alert" className="dashboard-empty-row">{what} unavailable. <button type="button" onClick={retry}>Retry</button></div>;
}

function AwardDate({ date }: { date: string }) {
  return <time dateTime={date}>{new Date(date).toLocaleDateString("en-AU", { timeZone: "Australia/Melbourne" })}</time>;
}

function RecentProficiencies({ uid }: { uid: string }) {
  const earned = usePublicData(`profile-proficiencies:${uid}`, () => fetchFeed({ pilot_uid: uid, kind: "proficiency", limit: SECTION_LIMIT }));
  if (earned.loading) return <p role="status" className="dashboard-empty-row">Loading…</p>;
  if (earned.error) return <Unavailable what="Proficiencies are" retry={earned.refresh} />;
  const items = earned.data?.items ?? [];
  if (!items.length) return <Empty>No proficiencies earned yet.</Empty>;
  return (
    <ul className="dashboard-achievement-list">
      {items.map((item) => (
        <li className="dashboard-achievement-row" key={item.id}>
          <div className="dashboard-proficiency-head">
            <div className="dashboard-achievement-title">{item.metadata.proficiency_title || item.title}</div>
            <AwardDate date={item.occurred_at} />
          </div>
          <div className="dashboard-achievement-meta"><span>{String(item.summary_vars?.level_display || item.metadata.level_reached || "")}</span></div>
        </li>
      ))}
    </ul>
  );
}

function RecentAchievements({ uid }: { uid: string }) {
  const achievements = usePublicData(`profile-achievements:${uid}`, () => fetchAchievements(uid));
  if (achievements.loading) return <p role="status" className="dashboard-empty-row">Loading…</p>;
  if (achievements.error) return <Unavailable what="Achievements are" retry={achievements.refresh} />;
  const earned = (achievements.data?.achievements ?? [])
    .filter((a) => a.earned_at)
    .sort((a, b) => (b.earned_at || "").localeCompare(a.earned_at || "") || a.id.localeCompare(b.id))
    .slice(0, SECTION_LIMIT);
  if (!earned.length) return <Empty>No achievements earned yet.</Empty>;
  return (
    <ul className="dashboard-achievement-list">
      {earned.map((a) => (
        <li className="dashboard-achievement-row" key={a.id}>
          <div className="dashboard-proficiency-head">
            <div className="dashboard-achievement-title">
              <span className="dashboard-achievement-icon" aria-hidden="true">{ACHIEVEMENT_ICONS[a.id] || DEFAULT_ACHIEVEMENT_ICON}</span>
              {a.name}
            </div>
            <AwardDate date={a.earned_at!} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function RecentHighScores({ uid }: { uid: string }) {
  const summary = usePublicData(`profile-gauges:${uid}`, () => fetchGaugeSummary(uid));
  if (summary.loading) return <p role="status" className="dashboard-empty-row">Loading…</p>;
  if (summary.error) return <Unavailable what="High scores are" retry={summary.refresh} />;
  const entries = (summary.data?.gauges ?? [])
    .filter((entry) => entry.pilot && GAUGE_BY_ID.has(entry.gauge_id))
    .sort((a, b) => (b.pilot!.recorded_at ?? "").localeCompare(a.pilot!.recorded_at ?? ""))
    .slice(0, SECTION_LIMIT);
  if (!entries.length) return <Empty>No high scores yet.</Empty>;
  return (
    <ul className="dashboard-achievement-list">
      {entries.map((entry) => {
        const gauge = GAUGE_BY_ID.get(entry.gauge_id)!;
        const mine = entry.pilot!;
        return (
          <li className="dashboard-achievement-row" key={entry.gauge_id}>
            <div className="dashboard-proficiency-head">
              <div className="dashboard-achievement-title">{gauge.label}</div>
              <span className="profile-score">{gauge.format(mine.value)}</span>
            </div>
            <div className="dashboard-achievement-meta">
              {mine.rank != null && mine.of != null && <span>{ordinal(mine.rank)} of {mine.of}</span>}
              {mine.recorded_at && <span>{formatRelativeTime(mine.recorded_at)}</span>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function StatCards({ stats }: { stats: ReturnType<typeof usePilotStats> }) {
  if (stats.loading) return <p role="status" className="dashboard-empty-row">Loading…</p>;
  if (stats.error || !stats.data) return <Unavailable what="Stats are" retry={stats.refresh} />;
  const { totalKills, totalDuration, totalLandings, carrierLandings } = stats.data;
  const cards: Array<[string, string]> = [
    ["Kills", (totalKills?.event_count ?? 0).toLocaleString()],
    ["Flight time", formatDurationHoursRounded(totalDuration?.value_total ?? 0)],
    ["Landings", (totalLandings?.event_count ?? 0).toLocaleString()],
    ["Carrier landings", (carrierLandings?.event_count ?? 0).toLocaleString()],
  ];
  return (
    <dl className="profile-stat-cards">
      {cards.map(([label, value]) => (
        <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
      ))}
    </dl>
  );
}

function RecentActivity({ uid }: { uid: string }) {
  const feed = usePublicData(`profile-activity:${uid}`, () => fetchFeed({ pilot_uid: uid, limit: ACTIVITY_LIMIT }));
  if (feed.loading) return <p role="status" className="dashboard-empty-row">Loading…</p>;
  if (feed.error) return <Unavailable what="Activity is" retry={feed.refresh} />;
  const items = feed.data?.items ?? [];
  if (!items.length) return <Empty>No recent activity.</Empty>;
  return <FeedTable items={items} showPills={false} />;
}

function PinControl({ pilotUid }: { pilotUid: string }) {
  const { pinnedUid, pin, notice } = useLens();
  // Once pinned, the header chip owns the pin: it shows the pilot and offers Unpin.
  if (pinnedUid === pilotUid) return notice ? <div className="profile-actions"><span className="profile-actions-notice" role="status">{notice}</span></div> : null;
  return (
    <div className="profile-actions">
      <button type="button" className="pin-button" onClick={() => pin(pilotUid)}><PinIcon /> Pin this pilot</button>
    </div>
  );
}

const PilotPage = ({ pilotUid }: { pilotUid: string }) => {
  const identity = usePublicData(`pilot:${pilotUid}`, () => fetchPilot(pilotUid));
  const stats = usePilotStats(pilotUid, { gaugeIds: [] });
  const uid = encode(pilotUid);

  const pilotName = identity.data?.name || (identity.loading ? "Loading pilot…" : "Unknown pilot");
  const group = useMemo(() => (stats.data ? primaryAirframeGroup(stats.data) : null), [stats.data]);
  const heroPhoto: PhotoKey = (group && AIRFRAME_PHOTOS[group]) || "pilots";
  const lastSeen = identity.data?.last_seen_at;

  return (
    <PageLayout
      route="pilots"
      hero={{
        photo: heroPhoto,
        focus: "50% 42%",
        title: pilotName,
        body: lastSeen ? `Last seen ${formatRelativeTime(lastSeen)}` : undefined,
      }}
    >
      {identity.error && (
        <div className="alert" role="alert">
          {identity.error.includes("404") ? "This pilot no longer exists." : "Unable to load the pilot."}
          {" "}<button type="button" onClick={identity.refresh}>Retry</button>
        </div>
      )}

      <PinControl pilotUid={pilotUid} />

      <div className="profile-grid">
        <Section title="Next unlocks" href={`#/proficiencies?pilot=${uid}`}>
          <NextUnlocks pilotUid={pilotUid} heading={false} emptyMessage="No proficiency progress yet." />
        </Section>
        <Section title="Recent proficiencies" href={`#/proficiencies?pilot=${uid}`}>
          <RecentProficiencies uid={pilotUid} />
        </Section>
        <Section title="Recent achievements" href={`#/achievements?pilot=${uid}`}>
          <RecentAchievements uid={pilotUid} />
        </Section>
        <Section title="Recent high scores" href={`#/high_scores?pilot=${uid}`}>
          <RecentHighScores uid={pilotUid} />
        </Section>
        <Section title="Stats" href={`#/stats?pilot=${uid}`} wide>
          <StatCards stats={stats} />
        </Section>
        <Section title="Recent activity" href={`#/feed?pilot_uid=${uid}`} wide>
          <RecentActivity uid={pilotUid} />
        </Section>
      </div>
    </PageLayout>
  );
};

export default PilotPage;
